import { isManager } from "@/lib/access";
import { ForecastCard } from "@/components/obboard/ForecastCard";
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtAmount, fmtCompact, fmtDate, statusOf, STATUS_STYLES } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Wallet, Clock3, AlertOctagon, CheckCircle2, ArrowRight, TrendingUp, User, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import MarkPaidDialog from "@/components/MarkPaidDialog";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";

function Kpi({ label, value, sub, icon: Icon, accent, testid, to }) {
  const clickable = Boolean(to);
  const inner = (
    <div data-testid={testid} className={`kpi-card bg-white border border-slate-200 rounded-md p-5 h-full ${clickable ? "cursor-pointer" : ""}`}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className={`w-8 h-8 rounded-md grid place-items-center border ${accent}`}>
          <Icon className="w-4 h-4" strokeWidth={1.5} />
        </div>
      </div>
      <div className="mt-4 text-3xl font-semibold tracking-tight mono">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
      {clickable && (
        <div className="mt-3 text-[11px] uppercase tracking-wider text-slate-400 group-hover:text-slate-700">
          View list <ArrowRight className="w-3 h-3 inline-block ml-0.5" />
        </div>
      )}
    </div>
  );
  if (clickable) return <Link to={to} className="group block">{inner}</Link>;
  return inner;
}

const AXIS_TICK = { fill: "#64748b", fontSize: 11, fontFamily: "JetBrains Mono" };
const AXIS_LINE = { stroke: "#e2e8f0" };
const CHART_MARGIN = { top: 8, right: 16, left: 4, bottom: 0 };
const BAR_RADIUS = [3, 3, 0, 0];
const TOOLTIP_CURSOR = { fill: "rgba(15,23,42,0.04)" };
const CHART_BOX = { width: "100%", height: 280 };

function TrendTooltip({ active, payload, label, currency }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-sm px-3 py-2 text-xs shadow-sm">
      <div className="text-slate-500 uppercase tracking-wider text-[10px]">{label}</div>
      <div className="mono font-medium text-slate-900 mt-0.5">{fmtAmount(d.outgo, currency)}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{d.is_future ? "Projected" : d.is_current ? "This month" : "Historical"}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = isManager(user);
  const [scope, setScope] = useState(isAdmin ? "team" : "mine");
  const [stats, setStats] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [markPaidTarget, setMarkPaidTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u, o, t] = await Promise.all([
        api.get(`/dashboard/stats?scope=${scope}`),
        api.get(`/dashboard/upcoming?days=14&scope=${scope}`),
        api.get(`/dashboard/overdue?scope=${scope}`),
        api.get(`/dashboard/trend?months=12&scope=${scope}`),
      ]);
      setStats(s.data);
      setUpcoming(u.data);
      setOverdue(o.data);
      setTrend(t.data);
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  // When user info loads (or role changes), default admins to team scope once
  useEffect(() => {
    if (isManager(user) && scope === "mine") setScope("team");
    // eslint-disable-next-line
  }, [user?.role]);

  const markPaid = async (o) => {
    if (o.is_variable_amount) {
      setMarkPaidTarget(o);
      return;
    }
    try {
      await api.post(`/obligations/${o.id}/mark-paid`, {});
      toast.success(o.amount == null ? "Marked done — next due date advanced" : "Payment recorded — next due date advanced");
      load();
    } catch {
      toast.error("Failed to mark paid");
    }
  };

  const displayCcy = stats?.display_currency || "INR";
  const trendData = (trend?.months || []).map((m) => ({
    ...m,
    label: m.month.slice(2), // "26-08" style
  }));

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Overview</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Obligations Overview</h1>
          <p className="text-sm text-slate-500 mt-1">
            {scope === "team" ? "Rolled-up finances across the whole team." : "Your obligations at a glance."}
          </p>
        </div>
        <Link to="/obligations">
          <Button data-testid="dashboard-cta-add" className="bg-blue-700 hover:bg-blue-800 text-white gap-2">
            Add obligation <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </div>

      {isAdmin && (
        <div className="flex items-center gap-1 mb-6 bg-slate-100 rounded-md p-1 w-fit">
          <button data-testid="dashboard-scope-mine" onClick={() => setScope("mine")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition ${scope === "mine" ? "bg-white shadow-sm text-blue-700" : "text-slate-500"}`}><User className="w-3.5 h-3.5" /> My finances</button>
          <button data-testid="dashboard-scope-team" onClick={() => setScope("team")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition ${scope === "team" ? "bg-white shadow-sm text-blue-700" : "text-slate-500"}`}><Users className="w-3.5 h-3.5" /> Team finances</button>
        </div>
      )}

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <Kpi testid="kpi-monthly-outgo" to={`/obligations?scope=${scope}`} label={`Monthly outgo · ${displayCcy}`} value={loading ? "—" : fmtAmount(stats?.monthly_outgo, displayCcy)} sub={scope === "team" ? "Team-wide, normalized" : "Normalized across all frequencies"} icon={Wallet} accent="bg-slate-50 border-slate-200 text-slate-700" />
        <Kpi testid="kpi-upcoming" to={`/obligations?status=upcoming&scope=${scope}`} label="Upcoming (7d)" value={loading ? "—" : stats?.upcoming_count ?? 0} sub="Due within next 7 days" icon={Clock3} accent="bg-amber-50 border-amber-200 text-amber-700" />
        <Kpi testid="kpi-overdue" to={`/obligations?status=overdue&scope=${scope}`} label="Overdue" value={loading ? "—" : stats?.overdue_count ?? 0} sub="Needs immediate attention" icon={AlertOctagon} accent="bg-rose-50 border-rose-200 text-rose-700" />
        <Kpi testid="kpi-paid" to={`/obligations?status=paid-this-month&scope=${scope}`} label="Paid this month" value={loading ? "—" : stats?.paid_this_month ?? 0} sub={`of ${stats?.total_obligations ?? 0} tracked`} icon={CheckCircle2} accent="bg-emerald-50 border-emerald-200 text-emerald-700" />
      </section>

      <ForecastCard scope={scope} />

      <section data-testid="trend-section" className="mt-8 bg-white border border-slate-200 rounded-md">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            <div className="text-sm font-semibold tracking-tight">12-month outgo trend</div>
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mono">
            {displayCcy} · past 6 · next 6
          </div>
        </div>
        <div className="p-4">
          {trend && trendData.length ? (
            <div style={CHART_BOX} data-testid="trend-chart">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={{ width: 800, height: 280 }}>
                <BarChart data={trendData} margin={CHART_MARGIN}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false} />
                  <YAxis tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false} tickFormatter={(v) => fmtCompact(v, displayCcy)} width={60} />
                  <Tooltip content={<TrendTooltip currency={displayCcy} />} cursor={TOOLTIP_CURSOR} />
                  <Bar dataKey="outgo" radius={BAR_RADIUS}>
                    {trendData.map((m) => (
                      <Cell key={m.month} fill={m.is_current ? "#1d4ed8" : m.is_future ? "#93c5fd" : "#94a3b8"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="p-8 text-center text-sm text-slate-500">
              {loading ? "Loading trend…" : "No data yet. Add an obligation to see the outgo trend."}
            </div>
          )}
          <div className="flex items-center gap-4 mt-3 px-2 text-[11px] text-slate-500">
            <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-slate-400" /> Historical</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-blue-700" /> This month</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-blue-300" /> Projected</span>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <Panel title="Overdue" testid="panel-overdue" empty="Nothing overdue. Nice work." items={overdue} onPay={markPaid} scope={scope} currentUserId={user?.id} isAdmin={isManager(user)} />
        <Panel title="Upcoming · next 14 days" testid="panel-upcoming" empty="No upcoming payments in the next 14 days." items={upcoming} onPay={markPaid} scope={scope} currentUserId={user?.id} isAdmin={isManager(user)} />
      </section>

      <MarkPaidDialog
        open={!!markPaidTarget}
        onOpenChange={(v) => !v && setMarkPaidTarget(null)}
        obligation={markPaidTarget}
        onDone={() => { setMarkPaidTarget(null); load(); }}
      />
    </div>
  );
}

function Panel({ title, empty, items, onPay, testid, scope, currentUserId, isAdmin }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <div className="text-sm font-semibold tracking-tight">{title}</div>
        <div className="text-xs text-slate-500 mono">{items.length} items</div>
      </div>
      {items.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500">{empty}</div>
      ) : (
        <ul className="divide-y divide-slate-200">
          {items.map((o) => {
            const status = statusOf(o.next_due_date);
            const isOwner = !currentUserId || o.user_id === currentUserId || isAdmin;
            return (
              <li key={o.id} className="ledger-row px-5 py-3 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">{o.name}</span>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${STATUS_STYLES[status]}`}>{status.replace("-", " ")}</span>
                    {scope === "team" && o.owner_name && (
                      <span data-testid={`owner-${o.id}`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200">{o.owner_name}</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    <span className="uppercase tracking-wider">{o.category}</span>
                    <span className="mx-2">·</span>
                    <span className="mono">Due {fmtDate(o.next_due_date)}</span>
                  </div>
                </div>
                <div className="text-right mono text-sm">{o.amount != null ? fmtAmount(o.amount, o.currency) : <span className="text-slate-400">—</span>}</div>
                {isOwner && (
                  <Button data-testid={`mark-paid-${o.id}`} size="sm" variant="outline" onClick={() => onPay(o)}>{o.amount == null && !o.is_variable_amount ? "Mark done" : "Mark paid"}</Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
